import { useEffect, useState } from "react";
import { WriteConfirm } from "../shared/WriteConfirm";
import { updateLmeDealScenario } from "../../api";
import type { Scenario } from "../../types/lme";
import {
  LOAN_PRODUCTS,
  LIEN_POSITIONS,
  PROP_TYPES,
  OCCUPANCIES,
  US_STATES,
} from "./LmeFinalizeModal";

interface Props {
  dealId: string;
  scenario: Scenario;
  /** Called after a successful write — parent re-runs the match + toasts. */
  onSaved: (changedCount: number) => void;
  onCancel: () => void;
}

type FieldKind = "money" | "number" | "decimal" | "select";
interface FieldDef {
  key: keyof Scenario;
  label: string;
  kind: FieldKind;
  options?: readonly string[];
}

// The editable scenario INPUTS (the displayed chips). Computed values
// (LTV/CLTV/LTARV/"scored on") are intentionally absent — they recompute.
const FIELDS: FieldDef[] = [
  { key: "loanAmount", label: "Loan amount", kind: "money" },
  { key: "loanProduct", label: "Loan product", kind: "select", options: LOAN_PRODUCTS },
  { key: "lienPosition", label: "Lien position", kind: "select", options: LIEN_POSITIONS },
  { key: "fico", label: "FICO", kind: "number" },
  { key: "dscr", label: "DSCR", kind: "decimal" },
  { key: "propType", label: "Property type", kind: "select", options: PROP_TYPES },
  { key: "propState", label: "Property state", kind: "select", options: US_STATES },
  { key: "occupancy", label: "Occupancy", kind: "select", options: OCCUPANCIES },
  { key: "termMin", label: "Term min (mo)", kind: "number" },
  { key: "termMax", label: "Term max (mo)", kind: "number" },
  { key: "speedDays", label: "Speed to close (days)", kind: "number" },
];

/** Display a stored value for the before→after diff. */
function fmt(f: FieldDef, v: string | number | null): string {
  if (v == null || v === "") return "(none)";
  if (f.kind === "money") return "$" + Number(v).toLocaleString();
  return String(v);
}

/** Parse a field's input string into the value sent to Zoho. */
function parseVal(f: FieldDef, raw: string): string | number | null {
  const t = raw.trim();
  if (f.kind === "select") return t;
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Edit the deal's scenario INPUTS in place (LME-3). Two-phase: form → an
 * explicit before→after review (shared WriteConfirm) → write to Zoho via
 * /update-deal-scenario. Only changed fields are sent. Same boundary rules as
 * the rest of the WriteConfirm family.
 */
export function LmeScenarioEditDialog({ dealId, scenario, onSaved, onCancel }: Props) {
  const [vals, setVals] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of FIELDS) {
      const v = scenario[f.key] as string | number | null | undefined;
      init[f.key as string] = v == null ? "" : String(v);
    }
    return init;
  });
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !reviewing) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, reviewing]);

  // Diff the form against the scenario → change list + the minimal write payload.
  const changes: Array<{ label: string; from: string; to: string }> = [];
  const fields: Record<string, string | number | null> = {};
  for (const f of FIELDS) {
    const parsed = parseVal(f, vals[f.key as string] ?? "");
    const orig = (scenario[f.key] as string | number | null | undefined) ?? null;
    const same =
      f.kind === "select"
        ? (parsed || "") === (orig || "")
        : parsed === orig;
    if (!same) {
      fields[f.key as string] = parsed;
      changes.push({ label: f.label, from: fmt(f, orig), to: fmt(f, parsed) });
    }
  }
  const hasChanges = changes.length > 0;

  if (reviewing) {
    return (
      <WriteConfirm
        title="Save scenario changes to Zoho?"
        body={
          <div className="lme-scenario-edit-confirm-note">
            Writes to the Zoho deal and re-scores lenders. Any already-finalized
            email is left as-is.
          </div>
        }
        changes={changes}
        confirmLabel="SAVE TO ZOHO"
        onConfirm={async () => {
          await updateLmeDealScenario({
            deal_id: dealId,
            fields: fields as Partial<Scenario>,
          });
          onSaved(changes.length);
        }}
        onCancel={() => setReviewing(false)}
      />
    );
  }

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card crm-create-card lme-scenario-edit-card">
        <div className="write-confirm-title">Edit deal scenario</div>
        <div className="crm-create-body lme-scenario-edit-grid">
          {FIELDS.map((f) => (
            <label className="crm-create-field" key={f.key as string}>
              <span className="crm-create-label">{f.label}</span>
              {f.kind === "select" ? (
                <select
                  className="crm-create-input"
                  value={vals[f.key as string] ?? ""}
                  onChange={(e) =>
                    setVals((v) => ({ ...v, [f.key as string]: e.target.value }))
                  }
                >
                  <option value="">—</option>
                  {f.options!.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="crm-create-input"
                  type="number"
                  inputMode={f.kind === "decimal" ? "decimal" : "numeric"}
                  step={f.kind === "decimal" ? "0.01" : "1"}
                  value={vals[f.key as string] ?? ""}
                  onChange={(e) =>
                    setVals((v) => ({ ...v, [f.key as string]: e.target.value }))
                  }
                />
              )}
            </label>
          ))}
        </div>
        <div className="write-confirm-actions">
          <button className="write-confirm-cancel" onClick={onCancel}>
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={() => setReviewing(true)}
            disabled={!hasChanges}
            title={hasChanges ? "Review the changes before writing" : "No changes yet"}
          >
            REVIEW CHANGES →
          </button>
        </div>
      </div>
    </div>
  );
}
